import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Save } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { ImageActions } from '@xeger/quill-image-actions';
import QuillAutoDetectUrl from 'quill-auto-detect-url';
import api from '../api/axiosInstance';
import { PermissionManager } from '../utils/PermissionManager';
import { ProposalBoardFormData, ProposalBoardPost } from './types';
import './css/proposal-board.css';

// Quill에 모듈 등록
Quill.register('modules/imageActions', ImageActions);
Quill.register('modules/autoDetectUrl', QuillAutoDetectUrl);

interface ExistingFile {
  id: string; // UUID 문자열로 변경
  originalName: string;
  fileSize?: number;
  size?: number; // JSON 호환성
  fileType?: string;
  contentType?: string; // JSON 호환성
  downloadUrl?: string;
  url?: string; // JSON 호환성
}

const ProposalBoardWrite: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditMode = !!editId;

  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Quill 에디터 ref
  const quillRef = useRef<ReactQuill>(null);

  // 이미지 업로드 공통 함수 (툴바 버튼, Ctrl+V 모두 이 함수 사용)
  const uploadAndInsertImage = useCallback(async (file: File) => {
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('이미지 파일만 업로드할 수 있습니다. (JPG, PNG, GIF, WebP)');
      return;
    }

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      alert('이미지 파일 크기는 5MB 이하로 업로드해주세요.');
      return;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);

      const response = await api.post('/api/proposal-board/upload-image', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;
      const imageId = response.data.id;

      setUploadedImages(prev => [...prev, { url: imageUrl, name: file.name, id: imageId }]);

      const quill = quillRef.current?.getEditor();
      if (quill) {
        const range = quill.getSelection() || { index: quill.getLength() };

        const img = new window.Image();
        img.onload = () => {
          quill.insertEmbed(range.index, 'image', imageUrl);

          requestAnimationFrame(() => {
            const images = quill.root.querySelectorAll('img');
            const lastImage = images[images.length - 1] as HTMLImageElement;
            if (lastImage && lastImage.src === imageUrl) {
              const editorWidth = quill.root.clientWidth - 20;
              const fitWidth = Math.min(img.naturalWidth, editorWidth);
              lastImage.setAttribute('width', String(Math.round(fitWidth)));
            }
          });

          quill.setSelection(range.index + 1);
        };
        img.src = imageUrl;
      }
    } catch (error) {
      console.error('이미지 업로드 실패:', error);
      alert('이미지 업로드에 실패했습니다.');
    }
  }, []);

  // Quill 이미지 핸들러 - 툴바 버튼용
  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      uploadAndInsertImage(file);
    };
  }, [uploadAndInsertImage]);

  // Quill 에디터 설정
  const quillModules = useMemo(() => ({
    imageActions: {},
    autoDetectUrl: true,
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [imageHandler]);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list',
    'align',
    'link', 'image',
    'width', 'height'
  ];

  // 오늘 날짜를 YYYY-MM-DD 형식으로 가져오기
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState<ProposalBoardFormData>({
    version: '',
    grade: '',
    title: '',
    content: '',
    deployDate: getTodayDate() // 기본값: 오늘
  });

  const [files, setFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<ExistingFile[]>([]);
  const [existingImages, setExistingImages] = useState<ExistingFile[]>([]);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]); // UUID 문자열 배열로 변경

  // 이미지 삽입 관련 상태
  const [uploadedImages, setUploadedImages] = useState<{ url: string, name: string, id: string }[]>([]); // UUID 문자열로 변경

  const [errors, setErrors] = useState({
    version: '',
    grade: '',
    title: '',
    content: ''
  });

  // Ctrl+V 이미지 붙여넣기 처리 - 툴바 이미지 버튼과 동일한 경로로 서버 업로드
  // 부모 컨테이너에서 capture phase로 이벤트를 가로채서 Quill clipboard보다 먼저 처리
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editorContainer = quill.root.parentNode as HTMLElement;
    if (!editorContainer) return;

    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;

      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.stopPropagation(); // Quill clipboard에 이벤트 전달 차단
          e.preventDefault();
          const file = item.getAsFile();
          if (file) uploadAndInsertImage(file);
          return;
        }
      }
    };

    editorContainer.addEventListener('paste', handlePaste, true); // capture phase
    return () => editorContainer.removeEventListener('paste', handlePaste, true);
  }, [uploadAndInsertImage]);

  // 버전 형식 검증 (예: 1.0.1 또는 1.2.3.4)
  const validateVersion = (version: string): boolean => {
    if (version === '') return true; // 빈 값은 허용 (자동 생성)
    const versionRegex = /^\d+\.\d+\.\d+(\.\d+)?$/;
    return versionRegex.test(version);
  };

  // 현재 버전 상태 추가
  const [latestVersion, setLatestVersion] = useState<string>('3.4.0');

  // 등급에 따라 버전 계산하는 함수
  const calculateNextVersion = (currentVersion: string, grade: string): string => {
    const parts = currentVersion.split('.');
    if (parts.length !== 3) return currentVersion;

    let [major, minor, patch] = parts.map(Number);

    if (grade === '상') {
      major += 1;
      minor = 0;
      patch = 0;
    } else if (grade === '중') {
      minor += 1;
      patch = 0;
    } else if (grade === '하') {
      patch += 1;
    }

    return `${major}.${minor}.${patch}`;
  };

  // 초기 인증 체크 및 최신 버전 불러오기
  useEffect(() => {
    const initAuth = async () => {
      try {
        const userNameRes = await api.get('/api/UserName');
        const userInfo = userNameRes.data;
        console.log('User info from API:', userInfo);

        if (!userInfo.authenticated) {
          alert('로그인이 필요합니다.');
          navigate('/');
          return;
        }

        const userId = userInfo.id || '';
        console.log('Setting currentUserId:', userId);
        setCurrentUserId(String(userId));

        // ProposalBoard는 관리자만 작성/수정 가능
        if (!PermissionManager.canModifyAllBoardPosts()) {
          alert('권한이 없습니다. 관리자만 접근할 수 있습니다.');
          navigate('/proposal-board');
          return;
        }

        // 수정 모드가 아닐 때만 최신 버전 불러오기
        if (!isEditMode) {
          try {
            const versionRes = await api.get('/api/proposal-board/next-version');
            const currentVersion = versionRes.data.version;
            console.log('Latest version:', currentVersion);
            setLatestVersion(currentVersion);
          } catch (error) {
            console.error('Failed to fetch latest version:', error);
            setLatestVersion('3.4.0');
          }
        }

        setAuthChecked(true);
      } catch (error) {
        console.error('Auth check failed:', error);
        alert('인증에 실패했습니다. 다시 로그인해주세요.');
        navigate('/');
      }
    };

    initAuth();
  }, [isEditMode, navigate]);

  // 수정 모드일 때 게시글 불러오기
  useEffect(() => {
    const loadPost = async () => {
      if (!isEditMode) {
        return;
      }

      if (!authChecked || currentUserId === '') {
        console.log('인증 대기 중', { authChecked, currentUserId });
        return;
      }

      try {
        console.log('Loading post for edit:', editId);
        const response = await api.get(`/api/proposal-board/${editId}`);
        const post: ProposalBoardPost = response.data;
        console.log('Loaded post data:', post);

        // 폼 데이터 설정
        setFormData({
          id: post.id,
          version: post.version,
          grade: post.grade || '',
          title: post.title,
          content: post.content,
          deployDate: post.deployDate ? post.deployDate.split('T')[0] : getTodayDate()
        });

        if (post.files) {
          // 이미지와 일반 파일 분리
          const images = post.files.filter((file: ExistingFile) => {
            const fileType = file.fileType?.toLowerCase() || '';
            const fileName = file.originalName?.toLowerCase() || '';
            return fileType.startsWith('image/') ||
              fileName.endsWith('.jpg') ||
              fileName.endsWith('.jpeg') ||
              fileName.endsWith('.png') ||
              fileName.endsWith('.gif') ||
              fileName.endsWith('.webp');
          });
          const otherFiles = post.files.filter((file: ExistingFile) => {
            const fileType = file.fileType?.toLowerCase() || '';
            const fileName = file.originalName?.toLowerCase() || '';
            return !(fileType.startsWith('image/') ||
              fileName.endsWith('.jpg') ||
              fileName.endsWith('.jpeg') ||
              fileName.endsWith('.png') ||
              fileName.endsWith('.gif') ||
              fileName.endsWith('.webp'));
          });
          setExistingImages(images);
          setExistingFiles(otherFiles);
        }

        // 권한 체크
        if (!PermissionManager.canModifyAllBoardPosts() && post.authorId !== currentUserId) {
          alert('수정 권한이 없습니다.');
          navigate('/proposal-board');
        }
      } catch (error) {
        console.error('Failed to fetch post:', error);
        alert('게시글을 불러오는데 실패했습니다.');
        navigate('/proposal-board');
      }
    };

    loadPost();
  }, [editId, authChecked, currentUserId, isEditMode, navigate]);

  const validateForm = () => {
    const newErrors = {
      version: '',
      grade: '',
      title: '',
      content: ''
    };

    // 버전 형식 검증
    if (formData.version && !validateVersion(formData.version)) {
      newErrors.version = '형식이 올바르지 않습니다. 올바른 형식은 숫자.숫자.숫자 형식입니다 (예: 1.0.1, 1.2.3 또는 1.2.3.4).';
    }

    // 등급 검증
    if (!formData.grade) {
      newErrors.grade = '등급을 선택해주세요.';
    }

    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    } else if (formData.title.length > 100) {
      newErrors.title = '제목은 100자 이내로 입력해주세요.';
    }

    if (!formData.content.trim()) {
      newErrors.content = '내용을 입력해주세요.';
    }

    setErrors(newErrors);
    return !newErrors.version && !newErrors.grade && !newErrors.title && !newErrors.content;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      const totalFiles = files.length + existingFiles.length + existingImages.length - filesToDelete.length + selectedFiles.length;

      if (totalFiles > 5) {
        alert('최대 5개의 파일만 첨부할 수 있습니다.');
        return;
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      for (const file of selectedFiles) {
        if (file.size > maxSize) {
          alert(`파일 크기는 10MB 이하로 업로드해주세요. (${file.name})`);
          return;
        }
      }

      setFiles(prev => [...prev, ...selectedFiles]);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingFile = (fileId: string) => {
    console.log('Removing file:', fileId);
    setFilesToDelete(prev => {
      const updated = [...prev, fileId];
      console.log('Files marked for deletion:', updated);
      return updated;
    });
  };

  const restoreExistingFile = (fileId: string) => {
    console.log('Restoring file:', fileId);
    setFilesToDelete(prev => {
      const updated = prev.filter(id => id !== fileId);
      console.log('Files marked for deletion after restore:', updated);
      return updated;
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = new FormData();

      // 게시글 데이터를 JSON으로 추가
      const postData: ProposalBoardFormData = {
        version: formData.version,
        grade: formData.grade,
        title: formData.title,
        content: formData.content,
        deployDate: formData.deployDate ? `${formData.deployDate}T00:00:00` : undefined, // LocalDateTime 형식으로 변환
        imageIds: uploadedImages.map(img => img.id)
      };

      if (isEditMode && formData.id) {
        postData.id = formData.id;
        // 삭제할 파일 ID들 추가
        if (filesToDelete.length > 0) {
          postData.filesToDelete = filesToDelete;
          console.log('Files to delete in submission:', filesToDelete);
        }
      }

      console.log('Submitting post data:', postData);

      submitData.append('post', new Blob([JSON.stringify(postData)], { type: 'application/json' }));

      // 파일들 추가
      files.forEach(file => {
        submitData.append('files', file);
      });

      let response;
      if (isEditMode) {
        response = await api.put(`/api/proposal-board/${editId}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await api.post('/api/proposal-board', submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      alert(isEditMode ? '게시글이 수정되었습니다.' : '게시글이 작성되었습니다.');
      navigate(`/proposal-board/${response.data.id}`);
    } catch (error) {
      console.error('Submit failed:', error);
      alert(isEditMode ? '게시글 수정에 실패했습니다.' : '게시글 작성에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="board-container">
      <header className="board-header">
        <h1 className="board-title">
          {isEditMode ? '버전 관리 수정' : '버전 관리 작성'}
        </h1>
        <div className="header-right">
          <button
            className="back-btn"
            onClick={() => navigate('/proposal-board')}
          >
            <ArrowLeft size={20} />
            목록으로
          </button>
        </div>
      </header>

      <div className="board-content">
        <form onSubmit={handleSubmit} className="write-form">
          {/* 등급, 버전, 배포일 한 줄로 */}
          <div className="form-row three-columns">
            <div className="form-group">
              <label htmlFor="grade" className="form-label">
                등급 <span className="required">*</span>
              </label>
              <select
                id="grade"
                value={formData.grade}
                onChange={(e) => {
                  const selectedGrade = e.target.value;
                  setFormData(prev => {
                    const newData = { ...prev, grade: selectedGrade };

                    // 등급 선택 시 자동으로 버전 계산 (항상 DB 최신 버전 기준)
                    if (selectedGrade && !isEditMode) {
                      const newVersion = calculateNextVersion(latestVersion, selectedGrade);
                      console.log(`등급 변경: ${selectedGrade}, 기준 버전: ${latestVersion} → 새 버전: ${newVersion}`);
                      newData.version = newVersion;
                    }

                    return newData;
                  });
                  setErrors(prev => ({ ...prev, grade: '' }));
                }}
                className={`form-input ${errors.grade ? 'error' : ''}`}
              >
                <option value="">등급 선택</option>
                <option value="상">상 (+1.0.0)</option>
                <option value="중">중 (+0.1.0)</option>
                <option value="하">하 (+0.0.1)</option>
              </select>
              {errors.grade && <span className="error-text">{errors.grade}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="version" className="form-label">
                버전
              </label>
              <input
                type="text"
                id="version"
                value={formData.version}
                onChange={(e) => {
                  const value = e.target.value;
                  // 빈 값이거나 0-9와 점(.)만 허용
                  if (value === '' || /^[0-9.]*$/.test(value)) {
                    setFormData(prev => ({ ...prev, version: value }));

                    // 실시간 형식 검증
                    if (value !== '' && !validateVersion(value)) {
                      setErrors(prev => ({ ...prev, version: '형식이 올바르지 않습니다.' }));
                    } else {
                      setErrors(prev => ({ ...prev, version: '' }));
                    }
                  }
                }}
                placeholder="등급 선택 시 자동"
                className={`form-input ${errors.version ? 'error' : ''}`}
                maxLength={20}
                readOnly={!isEditMode}
              />
              {errors.version && <span className="error-text">{errors.version}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="deployDate" className="form-label">
                배포일
              </label>
              <input
                type="date"
                id="deployDate"
                value={formData.deployDate}
                onChange={(e) => setFormData(prev => ({ ...prev, deployDate: e.target.value }))}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="title" className="form-label">
              제목 <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="제목을 입력하세요"
              className={`form-input ${errors.title ? 'error' : ''}`}
              maxLength={100}
            />
            {errors.title && <span className="error-text">{errors.title}</span>}
          </div>


          <div className="form-group">
            <label className="form-label">
              내용 <span className="required">*</span>
            </label>
            <div className="quill-editor-container" spellCheck={false}>
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={formData.content}
                onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                modules={quillModules}
                formats={quillFormats}
                placeholder="내용을 입력하세요"
              />
            </div>
            {errors.content && <span className="error-text">{errors.content}</span>}
          </div>

          <div className="form-group">
            <label className="form-label">파일 첨부</label>
            <div className="file-upload-section">
              <input
                type="file"
                id="file-input"
                onChange={handleFileChange}
                multiple
                accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt"
                className="file-input-hidden"
              />
              <label htmlFor="file-input" className="file-upload-btn">
                <Upload size={18} />
                파일 선택
              </label>
              <span className="file-info">
                최대 5개, 각 파일당 10MB 이하
              </span>
            </div>

            {/* 기존 이미지 목록 (수정 모드) */}
            {isEditMode && existingImages.length > 0 && (
              <div className="existing-images">
                <h4>기존 첨부 이미지</h4>
                <div className="image-preview-grid">
                  {existingImages.map(file => (
                    <div
                      key={file.id}
                      className={`image-preview-item ${filesToDelete.includes(file.id) ? 'deleted' : ''}`}
                    >
                      <img
                        src={file.downloadUrl || `${process.env.REACT_APP_API_HOST}/api/proposal-board/image/${file.id}`}
                        alt={file.originalName}
                        className={`preview-image ${filesToDelete.includes(file.id) ? 'faded' : ''}`}
                      />
                      <span className="image-name">{file.originalName}</span>
                      <span className="file-size">({formatFileSize(file.size || file.fileSize || 0)})</span>
                      {filesToDelete.includes(file.id) ? (
                        <button
                          type="button"
                          onClick={() => restoreExistingFile(file.id)}
                          className="restore-btn"
                        >
                          복원
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => removeExistingFile(file.id)}
                          className="remove-btn"
                        >
                          <X size={16} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 기존 파일 목록 (수정 모드) */}
            {isEditMode && existingFiles.length > 0 && (
              <div className="existing-files">
                <h4>기존 첨부파일</h4>
                {existingFiles.map(file => (
                  <div
                    key={file.id}
                    className={`file-item ${filesToDelete.includes(file.id) ? 'deleted' : ''}`}
                  >
                    <span className="file-name">{file.originalName}</span>
                    <span className="file-size">({formatFileSize(file.size || file.fileSize || 0)})</span>
                    {filesToDelete.includes(file.id) ? (
                      <button
                        type="button"
                        onClick={() => restoreExistingFile(file.id)}
                        className="restore-btn"
                      >
                        복원
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeExistingFile(file.id)}
                        className="remove-btn"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* 새로 선택한 파일 목록 */}
            {files.length > 0 && (
              <div className="new-files">
                <h4>새 첨부파일</h4>
                {files.map((file, index) => (
                  <div key={index} className="file-item">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">({formatFileSize(file.size)})</span>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="remove-btn"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={loading}
              className="submit-btn"
            >
              <Save size={18} />
              {loading ? '저장 중...' : isEditMode ? '수정' : '작성'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/proposal-board')}
              className="cancel-btn"
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProposalBoardWrite;
